import React from "react";
import clsx from "clsx";

/**
 * The section heading used inside page bodies: an uppercase 18/20px line behind
 * a 3px red bar.
 *
 * It started life pasted twice inside OTCyberRange (the Testbed/OT Lab heading
 * and "Key Features"), and the product pages had drifted to a weaker variant —
 * 16px, no bar, sometimes glued into the same <p> as the sentence below it — so
 * the same rank of heading looked like two different things depending on which
 * page you were on. This is that heading, defined once.
 *
 * Two registers, both from the OT page:
 *  • one line  → white, for a plain section name ("Key Features")
 *  • two lines → red `label` above a white `children` tagline ("OT LAB" /
 *    "YOUR CONTROL ENVIRONMENT, FULLY VIRTUALIZED.")
 *
 * Not `BorderedHeading`: that one is the big 30–55px page/section title with a
 * 4–5px bar and the `headline-*` type scale. This is the smaller rank below it.
 */
export default function BarHeading({
  children,
  label,
  className,
}: {
  /** The heading text. White — the tagline line when `label` is set. */
  children: React.ReactNode;
  /** Optional red line above `children`. */
  label?: string;
  className?: string;
}) {
  return (
    <div className={clsx("border-l-[3px] border-primary pl-6 py-1", className)}>
      {label && (
        <p className="uppercase text-primary font-semibold text-[18px] sm:text-[20px] leading-[1.2] tracking-[0.15px]">
          {label}
        </p>
      )}
      <p className="uppercase text-text-highlight font-semibold text-[18px] sm:text-[20px] leading-[1.3] tracking-[0.15px]">
        {children}
      </p>
    </div>
  );
}
